// Enforce HTTPS in production, allow HTTP for local development
const getApiUrl = () => {
  const hostname = window.location.hostname

  // Default to localhost for development
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  if (isLocalhost) {
    return 'http://localhost:3001'
  }

  // For iceops.app domains (including subdomains), use same-origin API
  // This ensures cookies work correctly without cross-origin issues
  if (hostname.endsWith('.iceops.app') || hostname === 'iceops.app') {
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:'
    const host = window.location.host
    return `${protocol}//${host}`
  }

  // For other domains, use VITE_API_URL if set
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl) {
    return envUrl
  }

  // Fallback: use current origin
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:'
  const host = window.location.host
  return `${protocol}//${host}`
}

const API_URL = getApiUrl()

// Cookie helper functions for cross-subdomain authentication
const setCookie = (name, value, days = 7) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)

  // Get base domain for cookie (e.g., .iceops.app from myorg.iceops.app)
  const hostname = window.location.hostname
  let domain = ''

  // For production (e.g., iceops.app, myorg.iceops.app), set domain to .iceops.app
  if (!hostname.includes('localhost') && !hostname.includes('127.0.0.1')) {
    const parts = hostname.split('.')
    if (parts.length >= 2) {
      domain = `.${parts.slice(-2).join('.')}`
    }
  }

  const domainPart = domain ? `; domain=${domain}` : ''
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/${domainPart}; SameSite=Lax`
}

const getCookie = (name) => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
  }
  return null
}

const deleteCookie = (name) => {
  const hostname = window.location.hostname

  // Delete cookie for current domain (no domain attribute)
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`

  // For production domains, also delete with domain attribute for cross-subdomain cleanup
  if (!hostname.includes('localhost') && !hostname.includes('127.0.0.1')) {
    const parts = hostname.split('.')
    if (parts.length >= 2) {
      const baseDomain = parts.slice(-2).join('.')

      // Delete with .iceops.app (most common cross-subdomain format)
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${baseDomain}`

      // Delete with iceops.app (without leading dot, for some browsers)
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${baseDomain}`

      // Delete for current hostname specifically
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`
    }
  }
}

// Helper function to make API requests (exported for direct use in components)
export async function apiRequest(endpoint, options = {}) {
  const csrfToken = getCookie('csrf_token')

  const headers = {
    'Content-Type': 'application/json',
    ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
    ...options.headers,
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      credentials: 'include', // Always send HttpOnly cookies
      headers,
    })

    if (!response.ok) {
      // Handle rate limiting
      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.')
      }

      // Handle authentication errors
      if (response.status === 401) {
        const error = await response.json().catch(() => ({ error: 'Invalid credentials' }))

        // Check for session expiry codes
        if (error.code === 'TOKEN_EXPIRED' || error.code === 'SESSION_EXPIRED') {
          // Dispatch session expired event for UserContext to handle
          window.dispatchEvent(new CustomEvent('sessionExpired', { detail: error }))
          throw new Error(error.error || 'Session expired. Please sign in again.')
        }

        throw new Error(error.error || 'Invalid username or password')
      }

      // Handle other errors
      const error = await response.json().catch(() => ({ error: `Request failed (${response.status})` }))
      throw new Error(error.error || `Request failed with status ${response.status}`)
    }

    return response.json()
  } catch (error) {
    // Handle network errors
    if (error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check your connection.')
    }
    throw error
  }
}

// Auth API
export const auth = {
  signUp: async (email, password, name, phone, userType) => {
    const data = await apiRequest('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, phone, userType }),
    })
    // Server now sets secure HttpOnly cookie automatically
    // No client-side token storage needed
    return data
  },

  signIn: async (email, password) => {
    const data = await apiRequest('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    // Server now sets secure HttpOnly cookie automatically
    // No client-side token storage needed
    return data
  },

  signOut: async () => {
    // Call server logout endpoint to clear HttpOnly cookie
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' })
    } catch (error) {
      console.error('Logout error:', error)
    }
    // Clean up any remaining client-side data
    deleteCookie('token') // Remove old client-side cookie if exists
    deleteCookie('user')  // Remove old client-side cookie if exists
    localStorage.removeItem('token')  // Remove old localStorage if exists
    localStorage.removeItem('user')  // Remove old localStorage if exists
    localStorage.removeItem('currentOrganization')
    // Clear cached user data from sessionStorage
    sessionStorage.removeItem('iceops_user')
    sessionStorage.removeItem('iceops_auth')
  },

  getUser: async () => {
    // Fetch current user from server using HttpOnly cookie
    try {
      const response = await apiRequest('/api/auth/me')
      return response.user
    } catch (error) {
      // Return special object to indicate error type
      // This allows refreshUser to distinguish between "not authenticated" vs "network/rate limit error"
      const isRateLimitError = error.message?.includes('Too many') || error.message?.includes('429')
      const isNetworkError = error.message?.includes('Unable to connect') || error.message?.includes('fetch')

      if (isRateLimitError || isNetworkError) {
        // Return error indicator so caller can keep cached data
        return { _error: true, type: isRateLimitError ? 'rate_limit' : 'network' }
      }

      // Actual auth failure (401, etc.) - return null to clear auth
      return null
    }
  },

  updateProfile: async (name, phone) => {
    const data = await apiRequest('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ name, phone }),
    })
    // Server updates user data, no client-side storage needed
    return data
  },

  canCreateOrg: async () => {
    return apiRequest('/api/auth/can-create-org')
  },

  searchUsers: (query) => apiRequest(`/api/auth/users/search?q=${encodeURIComponent(query)}`),

  getAllUsers: () => apiRequest('/api/auth/users'),

  updateUserRole: (userId, role) => apiRequest(`/api/auth/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  }),

  resetUserPassword: (userId, newPassword) => apiRequest(`/api/auth/users/${userId}/reset-password`, {
    method: 'PUT',
    body: JSON.stringify({ new_password: newPassword }),
  }),

  deactivateUser: (userId) => apiRequest(`/api/auth/users/${userId}/deactivate`, {
    method: 'PUT',
  }),

  reactivateUser: (userId) => apiRequest(`/api/auth/users/${userId}/reactivate`, {
    method: 'PUT',
  }),

  changePassword: async (currentPassword, newPassword) => {
    return apiRequest('/api/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      }),
    })
  },

  deleteUser: (userId) => apiRequest(`/api/auth/users/${userId}`, {
    method: 'DELETE',
  }),

  createPlaceholderUser: (email, name, phone, teamId, position, subPosition, jerseyNumber, role) => apiRequest('/api/auth/users/create', {
    method: 'POST',
    body: JSON.stringify({
      email,
      name,
      phone,
      team_id: teamId,
      position,
      sub_position: subPosition,
      jersey_number: jerseyNumber,
      role
    }),
  }),

  isAuthenticated: async () => {
    // Check authentication by attempting to get current user
    // HttpOnly cookies are sent automatically
    try {
      const response = await apiRequest('/api/auth/me')
      return !!response.user
    } catch (error) {
      return false
    }
  },

  requestMagicLink: async (email) => {
    const data = await apiRequest('/api/auth/request-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
    return data
  },

  switchRole: async (role) => {
    const data = await apiRequest('/api/auth/switch-role', {
      method: 'POST',
      body: JSON.stringify({ role }),
    })
    return data
  },

  verifyMagicLink: async (token) => {
    const data = await apiRequest(`/api/auth/verify-magic-link?token=${encodeURIComponent(token)}`)
    // Server sets secure HttpOnly cookie automatically on successful verification
    return data
  },

  signUpPasswordless: async (email, name, phone, userType, carrier) => {
    const data = await apiRequest('/api/auth/signup-passwordless', {
      method: 'POST',
      body: JSON.stringify({ email, name, phone, userType, carrier }),
    })
    return data
  },
}

// Leagues API
export const leagues = {
  getAll: (showArchived = false) => {
    const currentOrg = getCurrentOrganization()
    const params = new URLSearchParams()
    if (showArchived) params.append('showArchived', 'true')
    if (currentOrg) params.append('organization_id', currentOrg.id)
    const queryString = params.toString()
    return apiRequest(`/api/leagues${queryString ? `?${queryString}` : ''}`)
  },
  getById: (id) => apiRequest(`/api/leagues/${id}`),
  getBySlug: (slug) => apiRequest(`/api/leagues/by-slug/${slug}`),
  getManagers: (id) => apiRequest(`/api/leagues/${id}/managers`),
  addManager: (id, email) => apiRequest(`/api/leagues/${id}/managers`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),
  removeManager: (id, userId) => apiRequest(`/api/leagues/${id}/managers/${userId}`, {
    method: 'DELETE',
  }),
  create: (data) => apiRequest('/api/leagues', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/api/leagues/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateOrganization: (id, organizationId) => apiRequest(`/api/leagues/${id}/organization`, {
    method: 'PATCH',
    body: JSON.stringify({ organization_id: organizationId }),
  }),
  archive: (id, archived) => apiRequest(`/api/leagues/${id}/archive`, {
    method: 'PATCH',
    body: JSON.stringify({ archived }),
  }),
  delete: (id) => apiRequest(`/api/leagues/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE' }),
  }),
}

// Seasons API
export const seasons = {
  getByLeague: (leagueId) => apiRequest(`/api/seasons/league/${leagueId}`),
  getActive: (leagueId) => apiRequest(`/api/seasons/league/${leagueId}/active`),
  getBySlug: (leagueSlug, seasonSlug) => apiRequest(`/api/seasons/by-slug/${leagueSlug}/${seasonSlug}`),
  getById: (id) => apiRequest(`/api/seasons/${id}`),
  create: (data) => apiRequest('/api/seasons', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/api/seasons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  archive: (id, archived) => apiRequest(`/api/seasons/${id}/archive`, {
    method: 'PATCH',
    body: JSON.stringify({ archived }),
  }),
  setActive: (id) => apiRequest(`/api/seasons/${id}/set-active`, {
    method: 'PATCH',
  }),
  delete: (id) => apiRequest(`/api/seasons/${id}`, {
    method: 'DELETE',
  }),
  getPaymentStats: (id) => apiRequest(`/api/seasons/${id}/payment-stats`),
  getPlayersPayments: (id) => apiRequest(`/api/seasons/${id}/players-payments`),
}

// Teams API
export const teams = {
  getAll: () => apiRequest('/api/teams'),
  create: (data) => apiRequest('/api/teams', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/api/teams/${id}`, {
    method: 'DELETE',
  }),
}

// Games API
export const games = {
  getAll: () => apiRequest('/api/games'),
  create: (data) => apiRequest('/api/games', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/api/games/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateScore: (id, homeScore, awayScore) => apiRequest(`/api/games/${id}/score`, {
    method: 'PUT',
    body: JSON.stringify({ home_score: homeScore, away_score: awayScore }),
  }),
  getStats: (id) => apiRequest(`/api/games/${id}/stats`),
  delete: (id) => apiRequest(`/api/games/${id}`, {
    method: 'DELETE',
  }),
  cancel: (id, reason, notifyPlayers = true) => apiRequest(`/api/games/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason, notify_players: notifyPlayers }),
  }),
  bulkCancel: (gameIds, reason, notifyPlayers = true) => apiRequest('/api/games/bulk-cancel', {
    method: 'POST',
    body: JSON.stringify({ game_ids: gameIds, reason, notify_players: notifyPlayers }),
  }),
  reschedule: (id, newDate, newTime, reason, notifyPlayers = true) => apiRequest(`/api/games/${id}/reschedule`, {
    method: 'POST',
    body: JSON.stringify({ new_date: newDate, new_time: newTime, reason, notify_players: notifyPlayers }),
  }),
  restore: (id, notifyPlayers = false) => apiRequest(`/api/games/${id}/restore`, {
    method: 'POST',
    body: JSON.stringify({ notify_players: notifyPlayers }),
  }),
}

// Rinks API
export const rinks = {
  getAll: () => apiRequest('/api/rinks'),
  create: (data) => apiRequest('/api/rinks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Players API
export const players = {
  getAll: () => apiRequest('/api/players'),
  getByTeam: (teamId) => apiRequest(`/api/players/team/${teamId}`),
  create: (data) => apiRequest('/api/players', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/api/players/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  transfer: (id, teamId) => apiRequest(`/api/players/${id}/transfer`, {
    method: 'PATCH',
    body: JSON.stringify({ team_id: teamId }),
  }),
  delete: (id) => apiRequest(`/api/players/${id}`, {
    method: 'DELETE',
  }),
  getHistory: (id) => apiRequest(`/api/players/${id}/history`),
  getHistoryByUser: (userId) => apiRequest(`/api/players/user/${userId}/history`),
  getStats: (id) => apiRequest(`/api/players/${id}/stats`),
  getLeagueStats: (leagueId, seasonId = null) => {
    const url = seasonId
      ? `/api/players/league/${leagueId}/stats?seasonId=${seasonId}`
      : `/api/players/league/${leagueId}/stats`
    return apiRequest(url)
  },
}

// Announcements API
export const announcements = {
  getActive: (leagueId) => apiRequest(`/api/announcements/league/${leagueId}`),
  getAll: (leagueId) => apiRequest(`/api/announcements/league/${leagueId}/all`),
  getSubRequests: (leagueId) => apiRequest(`/api/announcements/league/${leagueId}/sub-requests`),
  create: (data) => apiRequest('/api/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  createSubRequest: (leagueId, gameId, title, message) => apiRequest('/api/announcements', {
    method: 'POST',
    body: JSON.stringify({
      league_id: leagueId,
      game_id: gameId,
      title,
      message,
      announcement_type: 'sub_request',
    }),
  }),
  respondToSubRequest: (announcementId, message) => apiRequest(`/api/announcements/${announcementId}/notify-available`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  update: (id, data) => apiRequest(`/api/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/api/announcements/${id}`, {
    method: 'DELETE',
  }),
}

// Playoffs API
export const playoffs = {
  getByLeagueSeason: (leagueId, seasonId) => apiRequest(`/api/playoffs/league/${leagueId}/season/${seasonId}`),
  getActive: (leagueId, seasonId) => apiRequest(`/api/playoffs/league/${leagueId}/season/${seasonId}/active`),
  getById: (bracketId) => apiRequest(`/api/playoffs/${bracketId}`),
  create: (data) => apiRequest('/api/playoffs', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateMatch: (matchId, data) => apiRequest(`/api/playoffs/matches/${matchId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  toggleActive: (bracketId) => apiRequest(`/api/playoffs/${bracketId}/toggle`, {
    method: 'PUT',
  }),
  delete: (bracketId) => apiRequest(`/api/playoffs/${bracketId}`, {
    method: 'DELETE',
  }),
}

// CSV API
export const csv = {
  downloadRosterTemplate: () => {
    window.open(`${API_URL}/api/csv/templates/roster`, '_blank')
  },
  downloadScheduleTemplate: () => {
    window.open(`${API_URL}/api/csv/templates/schedule`, '_blank')
  },
  downloadStandingsTemplate: () => {
    window.open(`${API_URL}/api/csv/templates/standings`, '_blank')
  },
  uploadRoster: async (teamId, file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_URL}/api/csv/upload/roster/${teamId}`, {
      method: 'POST',
      credentials: 'include', // Send HttpOnly cookies
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }))
      throw new Error(error.error || 'Upload failed')
    }

    return response.json()
  },
  uploadSchedule: async (leagueId, file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_URL}/api/csv/upload/schedule/${leagueId}`, {
      method: 'POST',
      credentials: 'include', // Send HttpOnly cookies
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }))
      throw new Error(error.error || 'Upload failed')
    }

    return response.json()
  },
}

// Payments API
export const payments = {
  getByTeam: (teamId) => apiRequest(`/api/payments/team/${teamId}`),
  getByPlayer: (playerId) => apiRequest(`/api/payments/player/${playerId}`),
  create: (data) => apiRequest('/api/payments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  markPaid: (id, confirmationNumber = null, paymentNotes = null, paymentMethod = null) => apiRequest(`/api/payments/${id}/paid`, {
    method: 'PUT',
    body: JSON.stringify({
      confirmation_number: confirmationNumber,
      payment_notes: paymentNotes,
      payment_method: paymentMethod,
    }),
  }),
  markUnpaid: (id) => apiRequest(`/api/payments/${id}/unpaid`, {
    method: 'PUT',
  }),
  updateAmount: (id, amount) => apiRequest(`/api/payments/${id}/amount`, {
    method: 'PUT',
    body: JSON.stringify({ amount }),
  }),
  updatePaymentLink: (id, customPaymentLink) => apiRequest(`/api/payments/${id}/payment-link`, {
    method: 'PUT',
    body: JSON.stringify({ custom_payment_link: customPaymentLink }),
  }),
  applyDiscount: (id, discountType, discountAmount, discountReason) => apiRequest(`/api/payments/${id}/discount`, {
    method: 'PUT',
    body: JSON.stringify({
      discount_type: discountType,
      discount_amount: discountAmount,
      discount_reason: discountReason,
    }),
  }),
  applyProration: (id, joinDate) => apiRequest(`/api/payments/${id}/prorate`, {
    method: 'PUT',
    body: JSON.stringify({ join_date: joinDate }),
  }),
  removeDiscount: (id) => apiRequest(`/api/payments/${id}/discount`, {
    method: 'DELETE',
  }),
  delete: (id) => apiRequest(`/api/payments/${id}`, {
    method: 'DELETE',
  }),
}

// Insurance API
export const insurance = {
  getByPlayer: (playerId) => apiRequest(`/api/insurance/player/${playerId}`),
  getCurrentStatus: (playerId, leagueId) => apiRequest(`/api/insurance/player/${playerId}/league/${leagueId}/current`),
  getLeagueStatus: (leagueId) => apiRequest(`/api/insurance/league/${leagueId}/status`),
  create: (data) => apiRequest('/api/insurance', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  markPaid: (id, paymentMethod, confirmationNumber, notes) => apiRequest(`/api/insurance/${id}/paid`, {
    method: 'PUT',
    body: JSON.stringify({
      payment_method: paymentMethod,
      confirmation_number: confirmationNumber,
      notes,
    }),
  }),
  waive: (id, reason) => apiRequest(`/api/insurance/${id}/waive`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  }),
  calculateProration: (data) => apiRequest('/api/insurance/calculate-proration', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Sub Requests API
export const subRequests = {
  getAll: () => apiRequest('/api/sub-requests'),
  getByGame: (gameId) => apiRequest(`/api/sub-requests/game/${gameId}`),
  create: (data) => apiRequest('/api/sub-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  accept: (id, substitutePlayerId) => apiRequest(`/api/sub-requests/${id}/accept`, {
    method: 'PUT',
    body: JSON.stringify({ substitute_player_id: substitutePlayerId }),
  }),
  delete: (id) => apiRequest(`/api/sub-requests/${id}`, {
    method: 'DELETE',
  }),
}

// Team Captains API
export const teamCaptains = {
  getByTeam: (teamId) => apiRequest(`/api/team-captains/team/${teamId}`),
  add: (userId, teamId) => apiRequest('/api/team-captains', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, team_id: teamId }),
  }),
  remove: (userId, teamId) => apiRequest('/api/team-captains', {
    method: 'DELETE',
    body: JSON.stringify({ user_id: userId, team_id: teamId }),
  }),
}

// Organizations API
export const organizations = {
  getAll: () => apiRequest('/api/organizations'),
  getById: (id) => apiRequest(`/api/organizations/${id}`),
  create: (data) => apiRequest('/api/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/api/organizations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/api/organizations/${id}`, {
    method: 'DELETE',
  }),
  getMembers: (id) => apiRequest(`/api/organizations/${id}/members`),
  addMember: (id, email, role = 'member') => apiRequest(`/api/organizations/${id}/members`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  }),
  removeMember: (id, userId) => apiRequest(`/api/organizations/${id}/members/${userId}`, {
    method: 'DELETE',
  }),
  updateMemberRole: (id, userId, role) => apiRequest(`/api/organizations/${id}/members/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  }),
}

// Broadcasts API
export const broadcasts = {
  send: (data) => apiRequest('/api/broadcasts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getHistory: (scope, scopeId) => apiRequest(`/api/broadcasts/history/${scope}/${scopeId}`),
  getDetails: (broadcastId) => apiRequest(`/api/broadcasts/${broadcastId}/details`),
}

// Team Join Requests API
export const teamJoinRequests = {
  create: (teamId, position, subPosition, jerseyNumber, message) => apiRequest('/api/team-join-requests', {
    method: 'POST',
    body: JSON.stringify({
      team_id: teamId,
      position,
      sub_position: subPosition,
      jersey_number: jerseyNumber,
      message
    }),
  }),
  getMyRequests: () => apiRequest('/api/team-join-requests/my-requests'),
  getByTeam: (teamId) => apiRequest(`/api/team-join-requests/team/${teamId}`),
  getPendingByLeague: (leagueId) => apiRequest(`/api/team-join-requests/league/${leagueId}/pending`),
  approve: (requestId) => apiRequest(`/api/team-join-requests/${requestId}/approve`, {
    method: 'POST',
  }),
  reject: (requestId) => apiRequest(`/api/team-join-requests/${requestId}/reject`, {
    method: 'POST',
  }),
  cancel: (requestId) => apiRequest(`/api/team-join-requests/${requestId}`, {
    method: 'DELETE',
  }),
}

// Inbox API (unified conversations)
export const inbox = {
  getConversations: (options = {}) => {
    const params = new URLSearchParams()
    if (options.status) params.append('status', options.status)
    if (options.category) params.append('category', options.category)
    if (options.limit) params.append('limit', options.limit)
    if (options.offset) params.append('offset', options.offset)
    const query = params.toString()
    return apiRequest(`/api/inbox${query ? `?${query}` : ''}`)
  },
  getStats: () => apiRequest('/api/inbox/stats'),
  getConversation: (id) => apiRequest(`/api/inbox/${id}`),
  sendReply: (conversationId, message) => apiRequest(`/api/inbox/${conversationId}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  }),
  updateStatus: (conversationId, status) => apiRequest(`/api/inbox/${conversationId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
  markRead: (conversationId) => apiRequest(`/api/inbox/${conversationId}/read`, {
    method: 'POST',
  }),
}

// Helper to get/set current organization ID
export const getCurrentOrganization = () => {
  const orgStr = localStorage.getItem('currentOrganization')
  return orgStr ? JSON.parse(orgStr) : null
}

export const setCurrentOrganization = (organization) => {
  if (organization) {
    localStorage.setItem('currentOrganization', JSON.stringify(organization))
  } else {
    localStorage.removeItem('currentOrganization')
  }
}

// Payment Processor Integrations API
export const integrations = {
  // List all supported processors
  getProcessors: () => apiRequest('/api/integrations/processors'),

  // Get connected processors for an organization
  getOrgProcessors: (orgId) => apiRequest(`/api/integrations/org/${orgId}/processors`),

  // Set a processor as primary
  setPrimary: (orgId, provider) => apiRequest(`/api/integrations/org/${orgId}/processors/${provider}/primary`, {
    method: 'PUT',
  }),

  // Disconnect a processor
  disconnect: (orgId, provider) => apiRequest(`/api/integrations/org/${orgId}/processors/${provider}`, {
    method: 'DELETE',
  }),

  // Get Stripe Connect URL (redirect to this)
  getStripeConnectUrl: (orgId) => `/api/integrations/stripe/connect?org_id=${orgId}`,

  // Create a checkout session for a payment
  createCheckout: (paymentId, processor = 'stripe') => apiRequest('/api/integrations/checkout', {
    method: 'POST',
    body: JSON.stringify({ paymentId, processor }),
  }),

  // Get manual payment method configuration
  getPaymentConfig: (orgId) => apiRequest(`/api/integrations/org/${orgId}/payment-config`),

  // Update manual payment method configuration
  updatePaymentConfig: (orgId, config) => apiRequest(`/api/integrations/org/${orgId}/payment-config`, {
    method: 'PUT',
    body: JSON.stringify(config),
  }),
}

// Invitations API
export const invitations = {
  // Create a new invitation
  create: (data) => apiRequest('/api/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // List invitations (with optional filters)
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    return apiRequest(`/api/invitations${queryString ? '?' + queryString : ''}`)
  },

  // Get invitation info (public - for accept page)
  getInfo: (token) => apiRequest(`/api/invitations/info/${token}`),

  // Resend invitation
  resend: (id) => apiRequest(`/api/invitations/${id}/resend`, {
    method: 'POST',
  }),

  // Revoke invitation
  revoke: (id) => apiRequest(`/api/invitations/${id}`, {
    method: 'DELETE',
  }),

  // Accept invitation
  accept: (token, name) => apiRequest('/api/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ token, name }),
  }),
}
